import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2, Search, Link2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { massnahmenAPI, masterMeasuresAPI } from '../../lib/api';

const NO_LOCATION = '__none__';

export const AssignMassnahmenDialog = ({ measure, onClose, onSaved }) => {
  const open = !!measure;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allMassnahmen, setAllMassnahmen] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');

  useEffect(() => {
    if (!measure) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setSearch('');
        setLocationFilter('all');
        const [allRes, assignedRes] = await Promise.all([
          massnahmenAPI.getAll(),
          masterMeasuresAPI.getAssignedMassnahmen(measure.id),
        ]);
        if (cancelled) return;
        const list = allRes.data || [];
        const assignedIds = new Set((assignedRes.data || []).map((d) => d.id));
        // Sicherstellen, dass auch zugeordnete (ggf. inaktive/scope-fremde) Maßnahmen
        // in der Liste auftauchen.
        const byId = new Map(list.map((m) => [m.id, m]));
        (assignedRes.data || []).forEach((d) => { if (!byId.has(d.id)) byId.set(d.id, d); });
        setAllMassnahmen(Array.from(byId.values()));
        setSelected(assignedIds);
      } catch {
        if (!cancelled) toast.error('Maßnahmen konnten nicht geladen werden');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [measure]);

  const locations = useMemo(() => {
    const map = new Map();
    allMassnahmen.forEach((m) => {
      const id = m.location_id || NO_LOCATION;
      if (!map.has(id)) map.set(id, m.location_name || 'Ohne Standort');
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [allMassnahmen]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allMassnahmen
      .filter((m) => {
        if (locationFilter !== 'all') {
          const id = m.location_id || NO_LOCATION;
          if (id !== locationFilter) return false;
        }
        if (!q) return true;
        return (
          (m.name || '').toLowerCase().includes(q) ||
          (m.short_code || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) =>
        (a.location_name || '').localeCompare(b.location_name || '') ||
        (a.name || '').localeCompare(b.name || ''));
  }, [allMassnahmen, search, locationFilter]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filteredIds = filtered.map((m) => m.id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filteredIds.forEach((id) => next.delete(id));
      else filteredIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const save = async () => {
    try {
      setSaving(true);
      await masterMeasuresAPI.setAssignments(measure.id, Array.from(selected));
      toast.success(`${selected.size} Maßnahme(n) zugeordnet`);
      onSaved?.();
      onClose();
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-hidden flex flex-col" data-testid="assign-massnahmen-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            <span>Maßnahmen zuordnen{measure?.name ? ` — ${measure.name}` : ''}</span>
          </DialogTitle>
          <DialogDescription>
            Operative Maßnahmen an- oder abwählen. Speichern setzt die
            komplette Zuordnung in einem Schritt.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche Name / Kürzel"
              className="pl-8"
              data-testid="assign-search-input"
            />
          </div>
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger className="sm:w-56" data-testid="assign-location-filter">
              <SelectValue placeholder="Standort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Standorte</SelectItem>
              {locations.map((l) => (
                <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between text-sm">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleAllFiltered}
            disabled={filteredIds.length === 0}
            className="rounded-full"
            data-testid="assign-toggle-all"
          >
            {allFilteredSelected ? 'Keine (gefiltert)' : 'Alle (gefiltert)'}
          </Button>
          <span className="text-muted-foreground" data-testid="assign-selected-count">
            {selected.size} zugeordnet
          </span>
        </div>

        <div className="flex-1 overflow-y-auto rounded-lg border divide-y min-h-[200px]" data-testid="assign-list">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-10">
              <Loader2 className="h-4 w-4 animate-spin" /> Wird geladen …
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">Keine Maßnahme gefunden.</p>
          ) : (
            filtered.map((m) => {
              const assignCount = (m.parent_measure_ids || []).length;
              const hasAssignment = assignCount > 0;
              return (
              <label
                key={m.id}
                className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-secondary/30 ${hasAssignment ? 'bg-amber-50 dark:bg-amber-950/30 border-l-2 border-amber-400' : ''}`}
                data-testid={`assign-item-${m.id}`}
              >
                <Checkbox
                  checked={selected.has(m.id)}
                  onCheckedChange={() => toggle(m.id)}
                  data-testid={`assign-checkbox-${m.id}`}
                />
                <span className="flex-1 min-w-0">
                  <span className="font-medium">{m.name}</span>
                  {m.short_code && (
                    <Badge variant="outline" className="ml-2 text-xs font-mono">{m.short_code}</Badge>
                  )}
                  {hasAssignment && (
                    <Badge
                      variant="outline"
                      className="ml-2 text-xs border-amber-400 text-amber-700 dark:text-amber-300 gap-1"
                      data-testid={`assign-item-badge-${m.id}`}
                      title="Diese Maßnahme ist bereits mindestens einer übergeordneten Maßnahme zugeordnet"
                    >
                      <Link2 className="h-3 w-3" />
                      {assignCount} {assignCount === 1 ? 'Zuordnung' : 'Zuordnungen'}
                    </Badge>
                  )}
                  {m.is_active === false && (
                    <Badge variant="secondary" className="ml-2 text-xs">inaktiv</Badge>
                  )}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                  <MapPin className="h-3 w-3" />
                  {m.location_name || 'Ohne Standort'}
                </span>
              </label>
              );
            })
          )}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full w-full sm:w-auto">Abbrechen</Button>
          <Button onClick={save} disabled={saving || loading} className="rounded-full w-full sm:w-auto" data-testid="assign-save-btn">
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Speichern …</> : 'Zuordnung speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
